// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, { useEffect, useRef } from "react";
import {
  useLogger,
  useBackgroundBlur,
} from "amazon-chime-sdk-component-library-react";
import { DefaultVideoTransformDevice } from "amazon-chime-sdk-js";
import { useUserFullname } from "@platform/hooks/useUserFullname";
import { useDevices } from "../../../providers/DevicesProvider";
import PreviewVideoTile from "./PreviewVideoTile";
import {
  useVideoSession,
  useVideoMeetingMembers,
  useVideoMeetingAccess,
  usePlatformStore,
} from "@platform/store";
import { extractRoomMemberData } from "@core/features/video";

export const PreviewVideo: React.FC<React.PropsWithChildren> = (props) => {
  const logger = useLogger();
  const videoEl = useRef<HTMLVideoElement>(null);
  const blurDeviceRef = useRef<DefaultVideoTransformDevice | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const { selectedVideoInputDevice } = useDevices();

  const { isBackgroundBlurSupported, createBackgroundBlurDevice } =
    useBackgroundBlur();
  const isBackgroundBlurEnabled = usePlatformStore(
    (s) => s.isBackgroundBlurEnabled
  );

  const userFullname = useUserFullname();

  const { userName } = useVideoSession();
  const { roomMembersData } = useVideoMeetingMembers();
  const { roomAccess } = useVideoMeetingAccess();
  const user = usePlatformStore((s) => s.user);

  // Post-join: look up from the store's roomMembersData map
  // Pre-join: fall back to roomAccess.room_members (available before joining)
  const roomMemberData = userName
    ? extractRoomMemberData({ externalUserId: userName }, roomMembersData).roomMemberData
    : (roomAccess?.room_members?.find((m) => m.user_id === user?.id) ?? null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blurDeviceRef.current) {
        blurDeviceRef.current.onOutputStreamDisconnect();
        blurDeviceRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Handle video preview when device or blur state changes
  useEffect(() => {
    let cancelled = false;

    async function startPreview(): Promise<void> {
      if (!selectedVideoInputDevice || !videoEl.current) {
        return;
      }

      try {
        // Fade out current video before swapping streams
        if (videoEl.current) {
          videoEl.current.style.opacity = "0";
        }

        // Stop any existing blur device
        if (blurDeviceRef.current) {
          blurDeviceRef.current.onOutputStreamDisconnect();
          blurDeviceRef.current = null;
        }

        // Stop any existing stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }

        // Get new raw stream with the selected device.
        // Use `exact` so we get the actual selected camera or a clear failure,
        // rather than `ideal` which silently falls back to the default camera.
        const rawStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: selectedVideoInputDevice },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });

        if (cancelled) {
          rawStream.getTracks().forEach((track) => track.stop());
          return;
        }

        // Show raw stream immediately so user sees camera feed right away
        if (videoEl.current) {
          videoEl.current.srcObject = rawStream;
          videoEl.current.play().catch((err) => {
            if (err.name !== "AbortError") {
              logger.error(`Error playing video: ${err}`);
            }
          });
        }

        streamRef.current = rawStream;

        // Apply background blur if enabled and supported
        if (isBackgroundBlurEnabled && isBackgroundBlurSupported) {
          try {
            const blurDevice = (await createBackgroundBlurDevice(
              selectedVideoInputDevice
            )) as DefaultVideoTransformDevice;

            if (cancelled) {
              blurDevice.onOutputStreamDisconnect();
              rawStream.getTracks().forEach((track) => track.stop());
              return;
            }

            // Set up observer BEFORE transformStream to catch processingDidStart
            const processingReady = new Promise<boolean>((resolve) => {
              const timeout = setTimeout(() => resolve(false), 3000);
              blurDevice.addObserver({
                processingDidStart: () => {
                  clearTimeout(timeout);
                  resolve(true);
                },
                processingDidFailToStart: () => {
                  clearTimeout(timeout);
                  resolve(false);
                },
              });
            });

            const transformedStream =
              await blurDevice.transformStream(rawStream);

            if (cancelled) {
              blurDevice.onOutputStreamDisconnect();
              return;
            }

            // Wait for pipeline to confirm it's producing frames
            const started = await processingReady;

            if (cancelled) {
              blurDevice.onOutputStreamDisconnect();
              return;
            }

            if (started && videoEl.current) {
              blurDeviceRef.current = blurDevice;
              videoEl.current.srcObject = transformedStream;
              videoEl.current.play().catch((err) => {
                if (err.name !== "AbortError") {
                  logger.error(`Error playing blurred video: ${err}`);
                }
              });
            } else {
              // Pipeline didn't start — keep showing raw stream
              blurDevice.onOutputStreamDisconnect();
              logger.warn("Background blur pipeline did not start, falling back to raw stream");
            }
          } catch (blurError) {
            logger.error(
              `Failed to apply background blur to preview: ${blurError}`
            );
            // Fall back to raw stream (already showing)
          }
        }
      } catch (error) {
        logger.error(`Failed to start video preview: ${error}`);
      }
    }

    // Add a small delay to ensure the device selection has been processed
    const timeoutId = setTimeout(() => {
      startPreview();
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [selectedVideoInputDevice, isBackgroundBlurEnabled, logger]);

  return (
    <div className="w-full">
      <PreviewVideoTile
        {...props}
        ref={videoEl}
        className="h-full w-full"
        nameplate={userFullname}
        roomMemberData={roomMemberData}
        onPlaying={() => {
          if (videoEl.current) {
            videoEl.current.style.opacity = "1";
          }
        }}
      />
    </div>
  );
};

export default PreviewVideo;
